import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, TextField, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import {
  useAddLabelMutation,
  useLazyGetLabelByIdQuery,
  useUpdateLabelMutation,
} from "../services/label";
import { showError, showToast } from "../constants/toasts";

interface Props {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editId?: number | string | null;
  isEdit?: boolean;
  onSuccess?: () => void;
}

export default function AddLabel({ open, onClose, setOpen, editId = null, isEdit = false, onSuccess }: Props) {
  const [labelName, setLabelName] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [addLabel] = useAddLabelMutation();
  const [updateLabel] = useUpdateLabelMutation();
  const [getLabelById] = useLazyGetLabelByIdQuery();

  useEffect(() => {
    const init = async () => {
      if (open && isEdit && editId) {
        try {
          const idNum = typeof editId === "string" ? parseInt(editId, 10) : editId;
          if (!idNum || Number.isNaN(idNum)) return;
          const response = await getLabelById({ id: idNum }).unwrap();
          setLabelName(response?.data?.label_name || "");
        } catch (e: any) {
          showError(e?.data?.message || "Failed to load label");
        }
      } else if (open && !isEdit) {
        setLabelName("");
      }
    };
    init();
  }, [open, isEdit, editId, getLabelById]);

  const handleSubmit = async () => {
    setError(true);
    const trimmed = labelName.trim();
    if (!trimmed) {
      showError("Label name is required");
      return;
    }

    setIsLoading(true);
    try {
      const idNum = isEdit && editId ? (typeof editId === "string" ? parseInt(editId, 10) : editId) : undefined;
      const body = { label_name: trimmed, ...(idNum ? { id: idNum } : {}) } as any;
      if (isEdit && editId) {
        const res = await updateLabel({ body }).unwrap();
        if ((res as any)?.status === 200 || (res as any)?.status === 201) {
          showToast("Label updated successfully");
          onSuccess?.();
          setOpen(false);
        }
      } else {
        const res = await addLabel({ body }).unwrap();
        if ((res as any)?.status === 200 || (res as any)?.status === 201) {
          showToast("Label added successfully");
          onSuccess?.();
          setOpen(false);
        }
      }
    } catch (e: any) {
      showError(e?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setError(false);
    setLabelName("");
  };

  return (
    <Modal className="modal alertConfig_modal" open={open} onClose={handleClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{isEdit ? "Edit Label" : "Add Label"}</h2>
            <div
              className="btn-close"
              onClick={() => {
                setOpen(false);
                setLabelName("");
              }}
            >
              <CloseIcon />
            </div>
          </div>

          <form className="form" action="">
            <div className="control_group">
              <label>Label</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Enter Label"
                value={labelName}
                onChange={(e) => setLabelName(e.target.value)}
              />
              {error && !labelName.trim() ? (
                <h6 className="err_msg">Label name is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => {
                  setError(false);
                  setOpen(false);
                  setLabelName("");
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : isEdit ? "Update" : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
